import {
  AuthClient,
  CustomersClient,
  DocTypesClient,
  LocationsClient,
  MessagesClient,
  GraphicsClient,
  UserClient,
  HeartbeatClient,
  SelectivityClient,
  ReportingClient,
} from './client';
import { IErrorDetails } from '../features/interfaces';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { AnyAction } from 'redux';
import { handleNetworkError, signOut, handleTimeToAuthExpiry } from '../features/authSlice';
import { checkAndDismissToast, customToast, toastIds, updateToast } from '@/components/shared/Toasts';
import { toast } from 'react-toastify';

export default class Api {
  private static AuthClient: AuthClient | null = null;
  static getAuthClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): AuthClient {
    if (this.AuthClient === null) {
      this.AuthClient = new AuthClient((window as any).CSG_CONFIG.BASE_URL, { fetch: createWrappedFetch(dispatch) });
    }
    return this.AuthClient;
  }
  private static CustomersClient: CustomersClient | null = null;
  static getCustomersClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): CustomersClient {
    if (this.CustomersClient === null) {
      this.CustomersClient = new CustomersClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.CustomersClient;
  }
  private static DocTypesClient: DocTypesClient | null = null;
  static getDocTypesClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): DocTypesClient {
    if (this.DocTypesClient === null) {
      this.DocTypesClient = new DocTypesClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.DocTypesClient;
  }
  private static MessagesClient: MessagesClient | null = null;
  static getMessagesClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): MessagesClient {
    if (this.MessagesClient === null) {
      this.MessagesClient = new MessagesClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.MessagesClient;
  }
  private static LocationsClient: LocationsClient | null = null;
  static getLocationsClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): LocationsClient {
    if (this.LocationsClient === null) {
      this.LocationsClient = new LocationsClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.LocationsClient;
  }
  private static GraphicsClient: GraphicsClient | null = null;
  static getGraphicsClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): GraphicsClient {
    if (this.GraphicsClient === null) {
      this.GraphicsClient = new GraphicsClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.GraphicsClient;
  }
  private static UserClient: UserClient | null = null;
  static getUserClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): UserClient {
    if (this.UserClient === null) {
      this.UserClient = new UserClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.UserClient;
  }
  private static HeartbeatClient: HeartbeatClient | null = null;
  static getHeartbeatClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): HeartbeatClient {
    if (this.HeartbeatClient === null) {
      this.HeartbeatClient = new HeartbeatClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.HeartbeatClient;
  }
  private static SelectivityClient: SelectivityClient | null = null;
  static getSelectivityClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): SelectivityClient {
    if (this.SelectivityClient === null) {
      this.SelectivityClient = new SelectivityClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.SelectivityClient;
  }
  private static ReportsClient: ReportingClient | null = null;
  static getReportingClient(dispatch: ThunkDispatch<unknown, unknown, AnyAction>): ReportingClient {
    if (this.ReportsClient === null) {
      this.ReportsClient = new ReportingClient((window as any).CSG_CONFIG.BASE_URL, {
        fetch: createWrappedFetch(dispatch),
      });
    }
    return this.ReportsClient;
  }
}

function createWrappedFetch(
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
): (url: RequestInfo, init?: RequestInit) => Promise<Response> {
  return async (url: RequestInfo, init?: RequestInit) => {
    let result: Response;
    try {
      if (!init) {
        init = {};
      }
      init.credentials = 'include';
      const xsrfToken: string | undefined = document?.cookie
        ?.split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (xsrfToken) init.headers = { ...init.headers, 'X-XSRF-TOKEN': xsrfToken };
      result = await window.fetch(url, init);

      if (result.headers.has('CSG-AUTH-REFRESH')) {
        const sv = result.headers.get('CSG-AUTH-REFRESH');
        if (sv) {
          const val = parseInt(sv);
          dispatch(handleTimeToAuthExpiry(val));
          ApiCallTracking.approximateExp = val;
        }
      }
    } catch (err) {
      dispatch(handleNetworkError());
      throw err;
    }
    if (result.status === 401) {
      if (!result.url.includes('/auth/login')) dispatch(signOut());
      const errorJson = await result.text();
      const errorDetails: IErrorDetails & {
        showToast: boolean;
        autoCloseToast: boolean;
        isAuth: boolean;
        dismissOnNavigation: boolean;
      } = JSON.parse(errorJson);
      if (errorDetails.showToast) {
        customToast({
          content: errorDetails.userMessage,
          type: 'error',
          id: '401-unauthorized',
          autoClose: errorDetails.autoCloseToast,
          isAuth: errorDetails.isAuth,
          dismissOnNavigation: errorDetails.dismissOnNavigation,
        });
      }
      throw errorDetails;
    } else if (result.status !== 200 && result.status !== 204) {
      const errorJson = await result.text();
      const errorDetails: IErrorDetails & {
        showToast: boolean;
        autoCloseToast: boolean;
        isAuth: boolean;
        dismissOnNavigation: boolean;
      } = JSON.parse(errorJson);

      if (
        errorDetails.showToast &&
        !toast.isActive(toastIds.vantageQueries) &&
        !errorDetails.userMessage?.includes('search criteria')
      ) {
        if (!toast.isActive(toastIds.error)) {
          customToast({
            content: errorDetails.userMessage,
            type: 'error',
            id: errorDetails.userMessage?.includes('department') ? toastIds.department : toastIds.error,
            autoClose: errorDetails.autoCloseToast,
            isAuth: !errorDetails.isAuth,
            dismissOnNavigation: errorDetails.dismissOnNavigation,
          });
        } else {
          updateToast({
            id: toastIds.error,
            content: errorDetails.userMessage,
          });
        }
      }
      throw errorDetails;
    } else if (result.status === 200) {
      checkAndDismissToast(toastIds.error);
    }
    return result;
  };
}

export interface IApiInfo {
  approximateExp: number;
}

export const ApiCallTracking: IApiInfo = {
  approximateExp: 0,
};
